import apiClient from './apiService';

// --- Types ---

export type ActivityExtent = 'view' | 'view+modify' | 'view+modify+approve';

export const ACTIVITY_EXTENTS: ActivityExtent[] = [
  'view',
  'view+modify',
  'view+modify+approve',
];

export const ACTIVITY_EXTENT_LABELS: Record<ActivityExtent, string> = {
  'view': 'View',
  'view+modify': 'View + Modify',
  'view+modify+approve': 'View + Modify + Approve',
};

export interface Activity {
  id: number;
  activity_key: string | null;
  activity_name: string;
  short_description: string | null;
  module_code: string | null;
  sequence_no: number;
  is_active: number;
}

// A role grants a specific activity at a specific extent.
export interface RoleActivityAssignment {
  activity_id: number;
  extent: ActivityExtent;
  is_active?: number;
}

export interface UserAssignment {
  id: number;
  user_id: number;
  user_name: string;
  is_active: number;
  assigned_date: string | null;
  assigned_by: number | null;
  effective_date: string | null;
  notes: string | null;
  /** Root Entity Admin (set only by Super-Admin onboarding) — assignment is locked:
   *  cannot be removed by a non-Super-Admin (BR5). UI shows a system badge + lock
   *  instead of a delete icon. */
  is_root_admin?: boolean;
}

export interface AdminRole {
  id: number;
  role_name: string;
  role_description: string | null;
  is_active: number;
  is_approved: number;
  is_external?: boolean;
  /** Seeded protected role (Entity Administrator, External Auditor) — definition frozen, even for Super Admin. */
  is_system?: boolean;
  created_by: number | null;
  created_on: string | null;
  modified_by: number | null;
  modified_on: string | null;
  assigned_activity_ids: number[];
  assigned_activities: RoleActivityAssignment[];
  assignments: UserAssignment[];
}

// Module code to display name mapping
export const MODULE_NAMES: Record<string, string> = {
  ORG: 'Organisation',
  COM: 'Compliance',
  RSK: 'Risk',
  ADT: 'Audit',
  INC: 'Incident',
  GOV: 'Governance',
  SYS: 'System',
  CST: 'Costing',
  PRM: 'Project Management',
  USR: 'User Management',
};

// --- Service ---

class RolesService {
  private basePath = '/api/v1/usr';

  async getActivities(): Promise<Activity[]> {
    const response = await apiClient.get<any>(`${this.basePath}/activities`);
    return response.data?.data || response.data || [];
  }

  // Activities are a seeded, immutable catalogue — no create/update/delete.

  // Server-side paginated. Pass page/limit/search and read `total` for the
  // pager — every role stays reachable no matter how many exist (no client cap).
  async getRoles(
    params?: { page?: number; limit?: number; search?: string },
  ): Promise<{ data: AdminRole[]; total: number }> {
    const response = await apiClient.get<any>(`${this.basePath}/roles`, { params });
    const data: AdminRole[] = response.data?.data || [];
    const total: number = response.data?.total ?? data.length;
    return { data, total };
  }

  async createRole(data: { role_name: string; role_description?: string; is_external?: boolean }): Promise<AdminRole> {
    const response = await apiClient.post<any>(`${this.basePath}/roles`, data);
    return response.data?.data || response.data;
  }

  async updateRole(id: number, data: { role_name?: string; role_description?: string; is_active?: number; is_external?: boolean }): Promise<AdminRole> {
    const response = await apiClient.put<any>(`${this.basePath}/roles/${id}`, data);
    return response.data?.data || response.data;
  }

  async deleteRole(id: number): Promise<void> {
    await apiClient.delete(`${this.basePath}/roles/${id}`);
  }

  async updateRoleActivities(
    roleId: number,
    activities: RoleActivityAssignment[],
  ): Promise<void> {
    await apiClient.put(`${this.basePath}/roles/${roleId}/activities`, { activities });
  }

  async toggleRoleActivity(roleId: number, activityId: number): Promise<{ is_active: number }> {
    const response = await apiClient.put<any>(`${this.basePath}/roles/${roleId}/activities/${activityId}/toggle`);
    return response.data;
  }

  async assignUserToRole(
    roleId: number,
    userId: number,
    effectiveDate?: string | null,
    notes?: string | null,
  ): Promise<UserAssignment> {
    const response = await apiClient.post<any>(`${this.basePath}/roles/${roleId}/users`, {
      user_id: userId,
      effective_date: effectiveDate ?? null,
      notes: notes ?? null,
    });
    return response.data?.data || response.data;
  }

  async deactivateUserFromRole(roleId: number, userId: number): Promise<void> {
    await apiClient.delete(`${this.basePath}/roles/${roleId}/users/${userId}`);
  }

  async getUsersDropdown(): Promise<{ id: number; name: string; email: string }[]> {
    const response = await apiClient.get<any>(`${this.basePath}/users/dropdown/all`);
    return Array.isArray(response.data) ? response.data : response.data?.data || [];
  }

  async getUserRbac(userId: number): Promise<UserRbacRole[]> {
    const response = await apiClient.get<any>(`${this.basePath}/users/${userId}/rbac`);
    return response.data?.roles || [];
  }
}

export interface UserRbacActivity {
  id: number;
  activity_name: string;
  short_description: string | null;
  module_code: string | null;
  extent: ActivityExtent;
}

export interface UserRbacRole {
  id: number;
  role_name: string;
  role_description: string | null;
  activities: UserRbacActivity[];
}

const rolesService = new RolesService();
export default rolesService;
